import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useApi,
  useApiRequest,
  useError,
  usePermissions,
  useNotifications
} from '../../hooks'
import Columns from '../Columns'
import Info from '../Info'
import DataTable, { Row, RowData } from '../DataTable'
import DropdownMenu, { DropdownItem } from '../DropdownMenu'
import ContextMenu from '../ContextMenu'
import EditUserPersonalData from '../EditUserPersonalData'
import { IconPickerItem } from '../Form/IconField'
import NotificationsRenderer from '../NotificationsRenderer'
import {
  doc,
  deleteDoc,
  updateDoc,
  arrayRemove,
  getDoc
} from 'firebase/firestore'
import { db, authedJsonHeaders } from '../../firebase'
import Spinner from '../Spinner'
import toast from 'react-hot-toast'

function capitalize(s) {
  return s ? s[0].toUpperCase() + s.slice(1) : 'Unknown'
}

function UserRolesList({ userId, organizationId, roleBindings, reloadUser }) {
  const [client] = useApi()
  const setError = useError()

  const normalizedBindings = useMemo(() => {
    if (Array.isArray(roleBindings)) {
      return roleBindings
    }

    if (roleBindings?.arrayValue?.values) {
      return roleBindings.arrayValue.values.map((item) => ({
        id: Number(item.mapValue.fields.id.integerValue),
        role: {
          id: item.mapValue.fields.role.mapValue.fields.id.stringValue,
          name: item.mapValue.fields.role.mapValue.fields.name.stringValue
        },
        secretKey: item.mapValue.fields.secretKey?.nullValue || null
      }))
    }

    return []
  }, [roleBindings])

  // async function removeRole(userId, roleId) {
  // console.log("Removing role binding for user:", userId, roleId);

  //   // try {
  //   //   if (window.confirm('Are you sure you wish to delete this role binding?')) {
  //   //     await client(
  //   //       `admin/organizations/${organizationId}/user-role-bindings/delete`,
  //   //       { userId, roleId }
  //   //     );
  //   //     await reloadUser();
  //   //   }
  //   // } catch (err) {
  //   //   setError(err);
  //   // }
  // }

  async function removeRole(userId, roleId) {
    try {
      if (
        window.confirm('Are you sure you wish to delete this role binding?')
      ) {
        // 1️⃣ Delete from user-role-bindings subcollection
        await deleteDoc(
          doc(
            db,
            'organizations',
            String(organizationId),
            'user-role-bindings',
            `binding_${String(userId)}_${String(roleId)}`
          )
        )

        // 2️⃣ Fetch user document to get the exact roleBindings array
        const userRef = doc(db, 'users', userId)
        const userSnap = await getDoc(userRef)

        if (!userSnap.exists()) {
          console.error('User not found!')
          return
        }

        const userData = userSnap.data()
        const currentRoleBindings = userData.roleBindings || []

        const roleBindingToRemove = currentRoleBindings.find(
          (r) => String(r.role?.id) === String(roleId)
        )

        if (!roleBindingToRemove) {
          console.error('Role binding not found for removal.')
          return
        }

        // 3️⃣ Remove from user's roleBindings array
        await updateDoc(userRef, {
          roleBindings: arrayRemove(roleBindingToRemove)
        })

        // 4️⃣ Refresh UI
        await reloadUser()
      }
    } catch (err) {
      console.error('Error removing role:', err)
      setError(err)
    }
  }

  return (
    <DataTable
      columns={['Role', 'Actions']}
      className='is-fullwidth is-hoverable is-size-7'>
      {roleBindings &&
        roleBindings.arrayValue?.values?.map((item) => (
          <Row key={item.mapValue.fields.id.stringValue}>
            <RowData>
              {item.mapValue.fields.role.mapValue.fields.name.stringValue}
            </RowData>
            <RowData>
              <DropdownMenu label='Actions' small={true}>
                <DropdownItem
                  onClick={() =>
                    removeRole(
                      userId,
                      item.mapValue.fields.role.mapValue.fields.id.stringValue
                    )
                  }>
                  Delete
                </DropdownItem>
              </DropdownMenu>
            </RowData>
          </Row>
        ))}
    </DataTable>
  )
}

function UserGroupsList({ userId, organizationId, groupBindings, reloadUser }) {
  const [client] = useApi()
  const setError = useError()

  const normalizedGroupBindings = Array.isArray(groupBindings)
    ? groupBindings
    : Object.values(groupBindings || {})

  // async function removeFromGroup(userId, groupId) {
  //   console.log("Removing user from group:", userId, groupId);
  //   // try {
  //   //   if (window.confirm('Are you sure you wish to delete this group binding?')) {
  //   //     await client(
  //   //       `admin/organizations/${organizationId}/user-groups/delete-binding`,
  //   //       { userId, groupId }
  //   //     );
  //   //     await reloadUser();
  //   //   }
  //   // } catch (err) {
  //   //   setError(err);
  //   // }
  // }

  async function removeFromGroup(userId, groupId) {
    try {
      if (
        window.confirm('Are you sure you wish to delete this group binding?')
      ) {
        const userRef = doc(db, 'users', userId)

        const userSnap = await getDoc(userRef)
        if (!userSnap.exists()) {
          console.error('User not found!')
          return
        }

        const userData = userSnap.data()
        const currentGroupBindings = userData.groupBindings || []

        const groupBindingToRemove = currentGroupBindings.find(
          (g) => g.group?.id === groupId
        )

        if (!groupBindingToRemove) {
          console.error('Group binding not found for removal.')
          return
        }

        await updateDoc(userRef, {
          groupBindings: arrayRemove(groupBindingToRemove)
        })

        await reloadUser()
      }
    } catch (err) {
      console.error('Error removing group:', err)
      setError(err)
    }
  }
  return (
    <DataTable
      columns={['Group', 'Actions']}
      className='is-fullwidth is-hoverable is-size-7'>
      {groupBindings &&
        groupBindings.arrayValue?.values?.map((item, i) => (
          <Row key={i}>
            <RowData>
              {item.mapValue.fields.group.mapValue.fields.name.stringValue}
            </RowData>
            <RowData>
              <DropdownMenu label='Actions' small={true}>
                <DropdownItem
                  onClick={() =>
                    removeFromGroup(
                      userId,
                      item.mapValue.fields.group.mapValue.fields.id.stringValue
                    )
                  }>
                  Remove
                </DropdownItem>
              </DropdownMenu>
            </RowData>
          </Row>
        ))}
    </DataTable>
  )
}

export default function ({ id }) {
  const navigate = useNavigate()
  const [client] = useApi()
  const [user, reloadUser] = useApiRequest(`users/${id}`)
  const [editing, setEditing] = useState()
  const setError = useError()
  const updateUserPermission = usePermissions('user', 'updateAny')
  const deleteUserPermission = usePermissions('user', 'deleteAny')
  const addNotification = useNotifications()

  const setOffDuty = useCallback(async () => {
    try {
      if (
        window.confirm('Are you sure you wish to set this user to off-duty?')
      ) {
        // ✅ Update the user's availability in Firestore
        const userRef = doc(db, 'users', id)
        await updateDoc(userRef, {
          // availability: "off-duty"
          dutyStatus: false
        })

        await reloadUser() // ✅ Your existing reload logic
        addNotification(
          <div>
            <div>User #{id} has been set to off-duty.</div>
          </div>,
          { className: 'is-success' }
        )
      }
    } catch (err) {
      setError(err)
    }
  }, [id, addNotification, reloadUser, setError])

  // async function deleteUser() {
  //   try {
  //     if (window.confirm('Are you sure you wish to delete this user?')) {
  //       await deleteDoc(doc(db, 'users', id))
  //       navigate(
  //         `/organizations/${
  //           user?.organization?.mapValue?.fields?.id?.integerValue ||
  //           user?.organization?.mapValue?.fields?.id?.stringValue ||
  //           ''
  //         }`
  //       )
  //     }
  //   } catch (err) {
  //     setError(err)
  //   }
  // }

  async function deleteUser() {
    try {
      const headers = await authedJsonHeaders()
      if (!headers) {
        throw new Error('Your session has expired. Please sign in again.')
      }

      const response = await fetch(
        `https://us-central1-live-alarm-v2.cloudfunctions.net/${
          process.env.REACT_APP_ENV === 'production'
            ? 'deleteUserProd'
            : 'deleteUserStage'
        }`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ userId: id })
        }
      )

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete user.')
      }

      toast.success('User deleted successfully.')

      navigate(
        `/organizations/${
          user?.organization?.mapValue?.fields?.id?.integerValue ||
          user?.organization?.mapValue?.fields?.id?.stringValue ||
          ''
        }`
      )
    } catch (err) {
      console.error(err)
      setError(err)
      toast.error(err.message || 'Error deleting user.')
    }
  }

  async function finishEditing() {
    await reloadUser()
    setEditing(false)
  }

  if (user === null) {
    return <Spinner />
  }

  if (!user) {
    return (
      <div className='notification is-warning'>
        User data not found. Please check if the user exists or try again later.
      </div>
    )
  }

  return (
    <div style={{ width: '100%' }}>
      <ContextMenu
        renderTitle={() => (
          <h1 className='title' style={{ paddingLeft: '1rem' }}>
            {user.email?.stringValue || 'N/A'}
          </h1>
        )}>
        <DropdownMenu label='Actions'>
          {updateUserPermission && updateUserPermission.granted && (
            <DropdownItem onClick={() => setEditing(true)}>
              Edit personal data
            </DropdownItem>
          )}
          {updateUserPermission && updateUserPermission.granted && (
            <DropdownItem
              onClick={() => navigate(`/users/${id}/create-role-binding`)}>
              Assign a role
            </DropdownItem>
          )}
          {updateUserPermission && updateUserPermission.granted && (
            <DropdownItem
              onClick={() => navigate(`/users/${id}/create-group-binding`)}>
              Assign to group
            </DropdownItem>
          )}
          {updateUserPermission && updateUserPermission.granted && (
            <DropdownItem
              onClick={() => navigate(`/users/${id}/set-user-type`)}>
              Change the user's type
            </DropdownItem>
          )}
          {updateUserPermission &&
            updateUserPermission.granted &&
            user?.dutyStatus?.booleanValue && (
              <DropdownItem onClick={() => setOffDuty()}>
                Set availability to off-duty
              </DropdownItem>
            )}
          {deleteUserPermission && deleteUserPermission.granted && (
            <DropdownItem onClick={() => deleteUser()}>Delete</DropdownItem>
          )}
        </DropdownMenu>
      </ContextMenu>
      <div className='box'>
        <h2 className='subtitle'>User Details</h2>
        <Columns>
          <Info label='ID'>{id || 'N/A'}</Info>
          <Info label='Email'>{user.email?.stringValue || 'N/A'}</Info>
          <Info label='Organization'>
            {user.organization?.mapValue?.fields?.name?.stringValue || 'N/A'}
          </Info>
          {user.userType && (
            <Info label='Type'>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center'
                }}>
                {/* <IconPickerItem
                  icon={user.userType?.mapValue?.fields?.icon?.stringValue}
                  size={24}
                  color={user.userType?.mapValue?.fields?.color?.stringValue}
                /> */}
                {user.userType?.mapValue?.fields?.name?.stringValue ||
                  'Standard User'}
              </div>
            </Info>
          )}
          <Info label='Availability'>
            {capitalize(user.dutyStatus?.booleanValue ? 'On-duty' : 'Off-duty')}
          </Info>
        </Columns>

        {!editing && (
          <Columns>
            <Info label='Full Name'>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center'
                }}>
                {user?.firstName?.stringValue || user?.lastName?.stringValue
                  ? `${user?.firstName?.stringValue || ''} ${
                      user?.lastName?.stringValue || ''
                    }`.trim()
                  : 'N/A'}
              </div>
            </Info>

            {[
              { field: 'displayName', label: 'Badge' },
              { field: 'phone', label: 'Phone Number' }
            ].map(
              ({ field, label }) =>
                user[field]?.stringValue && (
                  <Info key={field} label={label}>
                    {user[field]?.stringValue}
                  </Info>
                )
            )}
          </Columns>
        )}
        {editing && (
          <EditUserPersonalData id={id} onFinishEditing={finishEditing} />
        )}
      </div>
      {user.groupBindings?.arrayValue?.values?.length > 0 && (
        <div className='box'>
          <h2 className='subtitle'>Group Bindings</h2>
          <UserGroupsList
            userId={id}
            organizationId={
              user.organization?.mapValue?.fields?.id?.integerValue
            }
            groupBindings={user.groupBindings}
            reloadUser={reloadUser}
          />
        </div>
      )}
      {user.roleBindings?.arrayValue?.values?.length > 0 && (
        <div className='box'>
          <h2 className='subtitle'>Role Bindings</h2>
          <UserRolesList
            userId={id}
            organizationId={
              user.organization?.mapValue?.fields?.id?.integerValue
            }
            roleBindings={user.roleBindings}
            reloadUser={reloadUser}
          />
        </div>
      )}
      <NotificationsRenderer
        containerClassName='notifications'
        itemClassName='notification'
      />
    </div>
  )
}
